import React, { useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import moment from "moment";
import { useAuth } from "../../../provider/AuthProvider.tsx";
import SectionHeader from "../../../components/SectionHeader/SectionHeader.tsx";
import SelectGroup from "../../../components/Forms/SelectGroup/SelectGroup.tsx";
import DatePicker from "../../../components/Forms/DatePicker/DatePicker.tsx";
import FadeAlert, {
  FadeAlertType,
} from "../../../components/FadeAlert/FadeAlert.tsx";
import { inputClass, labelClass } from "../../../constants/formStyles.ts";
import type { CalcNavigationState } from "../../../types/calculatorTypes.ts";
import {
  OzkIntegrationService,
  OzkIssueRequest,
  OzkPadej,
  OzkTariffRequest,
} from "../../../services/Integrations/OzkIntegraton.ts";

// ─── Types ────────────────────────────────────────────────────────────────────

interface OzkForm {
  // Vehicle
  vhRegNo: string;
  vhRegCertNo: string;
  vhVin: string;
  vhManifactureYear: number;
  vhSeatsCount: number;
  vhEngineCapacity: number;
  vhMaxWeightF1: number;
  vhMaxPower: number;
  vhPurposeId: string;
  vhRegistrationTypeId: string;
  vhInsObjTypeId: string;
  vhBrandDescription: string;
  vhModelDescription: string;
  // Client
  custPin: string;
  custName: string;
  custSurname: string;
  custFamily: string;
  custTypeId: string;
  custFtypeId: string;
  custLocationId: string;
  custPostcode: string;
  custStreet: string;
  // Policy
  contractDate: string;
  comencingDate: string;
  expiringDate: string;
  periodId: string;
  numberOfInstallments: number;
  payType: string;
  // Issue documents
  stikerSerial: string;
  sertifikatSerial: string;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

const fmt = (d: string) =>
  moment(d, ["DD-MM-YYYY", "YYYY-MM-DD"]).format("YYYY-MM-DD");
// OZK carries the ГФ document series in `sdocTypeId` (120 = sticker series G1,
// 81 = certificate series AA), so `serialNum` must be the NUMBER ONLY - no series
// prefix. Strip a leading 2-char series (e.g. "G1", "AA"/"АА"); if the user already
// entered just the number, the regex doesn't match and the value is unchanged.
// The series letters may be typed in Latin OR Cyrillic ("AA" vs "АА"), so both
// alphabets are matched. (serialNum is limited to 9 chars - "АА023397" would fail.)
const stripDocSeries = (s: string) =>
  s.trim().replace(/^[A-Za-zА-Яа-яЁё][A-Za-zА-Яа-яЁё0-9]/, "");
const fmtISO = (d: string) => {
  const parsed = moment.utc(d, ["DD-MM-YYYY", "YYYY-MM-DD"]);
  const now = moment.utc();
  return parsed
    .hour(now.hour())
    .minute(now.minute())
    .second(now.second())
    .millisecond(now.millisecond())
    .toISOString();
};

const Spinner: React.FC = () => (
  <svg className="h-4 w-4 animate-spin" viewBox="0 0 24 24" fill="none">
    <circle
      className="opacity-25"
      cx="12"
      cy="12"
      r="10"
      stroke="currentColor"
      strokeWidth="4"
    />
    <path
      className="opacity-75"
      fill="currentColor"
      d="M4 12a8 8 0 018-8v8H4z"
    />
  </svg>
);

const InfoRow: React.FC<{ label: string; value: React.ReactNode }> = ({
  label,
  value,
}) => (
  <div className="flex flex-col">
    <span className="text-body dark:text-bodydark text-[11px]">{label}</span>
    <span className="text-xs font-semibold text-black dark:text-white">
      {value ?? "-"}
    </span>
  </div>
);

// ─── InstallmentPicker ────────────────────────────────────────────────────────

const InstallmentPicker: React.FC<{
  padeji: OzkPadej[];
  total: number;
  selected: number;
  onSelect: (n: number) => void;
}> = ({ padeji, total, selected, onSelect }) => {
  // Group options by count: 1 instalment, 2 instalments, etc.
  const maxCount = padeji.length;
  // Build grouped options: for count N, show N instalments each = total/N
  const options = useMemo(() => {
    return Array.from({ length: maxCount }, (_, i) => {
      const count = i + 1;
      // Use actual padeji for this count if available (API may return exactly `count` entries)
      const slice = padeji.slice(0, count);
      return { count, instalments: slice };
    });
  }, [padeji, maxCount]);

  return (
    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
      {options.map(({ count, instalments }) => {
        const isActive = selected === count;
        // `total` is the authoritative calculatedPremium from the tariff response;
        // padeji entries drive only the breakdown (count + first due date).
        const perInstalment = count > 0 ? total / count : total;

        return (
          <button
            key={count}
            type="button"
            onClick={() => onSelect(count)}
            className={`group relative flex flex-col items-center rounded-sm border p-4 text-center transition-all duration-150 focus:outline-none ${
              isActive
                ? "border-[#243095] bg-[#243095] text-white shadow-md ring-2 ring-[#243095]/20"
                : "border-stroke dark:border-strokedark dark:bg-boxdark bg-white hover:border-[#243095]/50 hover:shadow-sm"
            }`}
          >
            {/* Count badge */}
            <span
              className={`mb-2 flex h-8 w-8 items-center justify-center rounded-full text-sm font-bold ${
                isActive
                  ? "bg-white/20 text-white"
                  : "bg-[#243095]/10 text-[#243095]"
              }`}
            >
              {count}
            </span>

            <span
              className={`text-xs font-semibold uppercase tracking-wide ${
                isActive ? "text-white/80" : "text-body dark:text-bodydark"
              }`}
            >
              {count === 1 ? "вноска" : count < 5 ? "вноски" : "вноски"}
            </span>

            <div
              className={`mt-2 text-lg font-bold ${
                isActive ? "text-white" : "text-[#243095] dark:text-white"
              }`}
            >
              {perInstalment.toFixed(2)}
              <span
                className={`ml-1 text-xs font-normal ${
                  isActive ? "text-white/70" : "text-body"
                }`}
              >
                EUR
              </span>
            </div>

            {count > 1 && (
              <span
                className={`mt-0.5 text-[11px] ${
                  isActive ? "text-white/60" : "text-body dark:text-bodydark"
                }`}
              >
                Общо: {total.toFixed(2)} EUR
              </span>
            )}

            {/* First due date */}
            {instalments[0]?.padejDate && (
              <span
                className={`mt-2 text-[10px] ${
                  isActive ? "text-white/60" : "text-body"
                }`}
              >
                Първа:{" "}
                {new Date(instalments[0].padejDate).toLocaleDateString("bg-BG")}
              </span>
            )}

            {/* Active checkmark */}
            {isActive && (
              <span className="absolute right-2 top-2 flex h-5 w-5 items-center justify-center rounded-full bg-white/20">
                <svg
                  className="h-3 w-3 text-white"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={3}
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M5 13l4 4L19 7"
                  />
                </svg>
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
};

// ─── Main Component ───────────────────────────────────────────────────────────

const Ozk: React.FC = () => {
  const { jwt } = useAuth();
  const location = useLocation();
  const calcState = location.state as CalcNavigationState | undefined;

  // Nomenclatures
  const [cities, setCities] = useState<Record<string, string>>({});
  const [ftypes, setFtypes] = useState<Record<string, string>>({});
  const [custTypes, setCustTypes] = useState<Record<string, string>>({});
  const [vhPurposes, setVhPurposes] = useState<Record<string, string>>({});
  const [, setRegTypes] = useState<Record<string, string>>({});

  // Form
  const [form, setForm] = useState<OzkForm>(() => {
    const v = calcState?.vehicle;
    const o = calcState?.owner;
    // Normalized policyholder from the backend: carries per-provider classification
    // codes (providers.ozk = custTypeId/custFtypeId), the EKATTE settlement code and
    // split name parts, so nothing here is hardcoded/guessed. Falls back to the raw
    // owner only when the aggregator didn't return a holder.
    const h = calcState?.holder;
    const ozkCodes = h?.providers?.ozk;

    const today = moment().format("DD-MM-YYYY");
    const commencing = moment().add(1, "day").format("DD-MM-YYYY");
    const expiring = moment().add(1, "year").add(1, "day").format("DD-MM-YYYY");

    // Split owner name into parts (Bulgarian convention: Name Surname Family).
    const nameParts = (o?.name ?? "").split(" ");
    const isLegal =
      (h?.customerType ?? (o?.personType === "2" ? "legal" : "natural")) ===
      "legal";

    return {
      vhRegNo: v?.regNo ?? "",
      vhRegCertNo: v?.certNumber ?? "",
      vhVin: v?.chassis ?? "",
      vhManifactureYear: v?.productionYear ?? 0,
      vhSeatsCount: v?.seats ?? 4,
      vhEngineCapacity: v?.displacement ?? 0,
      // F.1 max mass (kg) consolidated by the backend; OZK requires it for
      // tariff & issue. Fall back to 2500 when no provider supplied a value.
      vhMaxWeightF1: v?.maxWeightF1 || 2500,
      vhMaxPower: v?.enginePower ?? 0,
      vhPurposeId: "1",
      vhRegistrationTypeId: "1",
      vhInsObjTypeId: "1",
      vhBrandDescription: v?.make ?? "",
      vhModelDescription: v?.model ?? "",
      custPin: h?.pin ?? o?.pid ?? "",
      custName: h?.firstName ?? nameParts[0] ?? "",
      custSurname: h?.surname ?? nameParts[1] ?? "",
      custFamily: h?.familyName ?? nameParts[2] ?? nameParts[1] ?? "",
      // custTypeId & custFtypeId are coupled: the ftype's parent (NOM_FTYPE.parentId)
      // must equal the custType. Prefer the backend-mapped codes (holder.providers.ozk);
      // else derive from person type - legal → "F" + "1020" (Частно юридическо лице),
      // natural → "C" + "2010" (Местно лице). A cross-parent mix (e.g. "C" + "1010",
      // whose parent is "F") is rejected by OZK issue with S001-004.
      custTypeId: ozkCodes?.custTypeId ?? (isLegal ? "F" : "C"),
      custFtypeId: ozkCodes?.custFtypeId ?? (isLegal ? "1020" : "2010"),
      // EKATTE settlement code - OZK requires custLocationId (S001-005 otherwise).
      custLocationId: h?.settlementCode ?? "",
      custPostcode: h?.postCode ?? "",
      custStreet: h?.street ?? o?.address ?? "",
      contractDate: today,
      comencingDate: commencing,
      expiringDate: expiring,
      periodId: "y001",
      numberOfInstallments: 1,
      payType: "A",
      stikerSerial: "",
      sertifikatSerial: "",
    };
  });

  // Tariff / installments
  const [padeji, setPadeji] = useState<OzkPadej[]>([]);
  // Authoritative total from the tariff response (calculatedPremium, EUR).
  const [calculatedPremium, setCalculatedPremium] = useState(0);
  const [tariffLoading, setTariffLoading] = useState(false);
  const [tariffError, setTariffError] = useState<string | null>(null);

  // Issue / print
  const [issueLoading, setIssueLoading] = useState(false);
  const [issuedReqId, setIssuedReqId] = useState<string | null>(null);
  const [issuedUpn, setIssuedUpn] = useState<string | null>(null);
  const [printLoading, setPrintLoading] = useState(false);

  // General alert
  const [alert, setAlert] = useState<string | null>(null);
  const [alertType, setAlertType] = useState<FadeAlertType>(FadeAlertType.info);

  const autoFetchedRef = useRef(false);

  // ── Load nomenclatures ────────────────────────────────────────────────────

  useEffect(() => {
    Promise.all([
      OzkIntegrationService.getAllCities(jwt),
      OzkIntegrationService.getAllFtypes(jwt),
      OzkIntegrationService.getAllCustomerTypes(jwt),
      OzkIntegrationService.getAllVehiclePurposes(jwt),
      OzkIntegrationService.getAllVehicleRegistrationTypes(jwt),
    ])
      .then(([c, f, ct, vp, rt]) => {
        setCities(c);
        setFtypes(f);
        setCustTypes(ct);
        setVhPurposes(vp);
        setRegTypes(rt);
      })
      .catch((e) => {
        setAlert("Грешка при зареждане на номенклатури: " + e.message);
        setAlertType(FadeAlertType.error);
      });
  }, []);

  // ── Auto-fetch tariff when arriving from calculator ───────────────────────

  useEffect(() => {
    if (calcState && !autoFetchedRef.current) {
      autoFetchedRef.current = true;
      // Small delay so nomenclatures can start loading
      setTimeout(() => fetchTariff(), 400);
    }
  }, [calcState]);

  // ── Form helpers ──────────────────────────────────────────────────────────

  const set = (field: keyof OzkForm, value: OzkForm[keyof OzkForm]) =>
    setForm((p) => ({ ...p, [field]: value }));

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value, type } = e.target;
    set(name as keyof OzkForm, type === "number" ? Number(value) : value);
  };

  // ── Build tariff payload ──────────────────────────────────────────────────

  const buildTariffPayload = (): OzkTariffRequest => ({
    contractDate: fmt(form.contractDate),
    comencingDate: fmtISO(form.comencingDate),
    expiringDate: fmtISO(form.expiringDate),
    periodId: form.periodId,
    vhTypeIntId: form.vhInsObjTypeId,
    custFtypeId: form.custFtypeId,
    custLocationId: form.custLocationId,
    custPin: form.custPin,
    custTrafficaccidents: "F",
    vhSeatsCount: form.vhSeatsCount,
    vhEngineCapacity: form.vhEngineCapacity,
    vhManifactureYear: form.vhManifactureYear,
    vhMaxWeightF1: form.vhMaxWeightF1,
    vhRegistrationTypeId: form.vhRegistrationTypeId,
    driverFtypeId: form.custFtypeId,
    driverPin: form.custPin,
    numberOfInstallments: form.numberOfInstallments,
    vhPurpose: form.vhPurposeId,
    vhRegNo: form.vhRegNo,
    vhRegCertNo: form.vhRegCertNo,
    vhInsObjTypeId: form.vhInsObjTypeId,
    acquirerFtypeId: "",
    acquirerPin: "",
    vehicleUserFtypeId: form.custFtypeId,
    vehicleUserPin: form.custPin,
    newOwnerPin: "",
  });

  // ── Fetch tariff ──────────────────────────────────────────────────────────

  const fetchTariff = async () => {
    setTariffLoading(true);
    setTariffError(null);
    setPadeji([]);
    setCalculatedPremium(0);
    setIssuedReqId(null);
    setIssuedUpn(null);

    try {
      const res = await OzkIntegrationService.getTariff(
        jwt,
        buildTariffPayload()
      );
      const instalments = res.padeji?.padej ?? [];
      if (!instalments.length) throw new Error("Не са получени падежи от ОЗК");
      setPadeji(instalments);
      setCalculatedPremium(res.calculatedPremium ?? 0);
    } catch (e: any) {
      setTariffError(e.message ?? "Грешка при тарифиране");
    } finally {
      setTariffLoading(false);
    }
  };

  // ── Build issue payload ───────────────────────────────────────────────────

  const buildIssuePayload = (): OzkIssueRequest => {
    const person = {
      custName: form.custName,
      custSurname: form.custSurname,
      custFamily: form.custFamily,
      custPin: form.custPin,
      custCountryId: "BG",
      custLocationId: form.custLocationId,
      // OZK validates the postcode against its OWN nomenclature and rejects even a
      // valid Bulgarian postcode when it doesn't match its record for the location
      // (S001-004 "Невалидна стойност … Пощенски код" - e.g. 2850/Petrich). It is
      // optional and OZK issues fine without it, and we have no OZK postcode map,
      // so we always send it empty rather than risk failing the whole issue.
      custPostcode: "",
      custStreet: form.custStreet,
      custFtypeId: form.custFtypeId,
    };

    return {
      mtplPolicy: {
        contractDate: fmt(form.contractDate),
        comencingDate: fmtISO(form.comencingDate),
        expiringDate: fmtISO(form.expiringDate),
        numberOfInstallments: form.numberOfInstallments,
        vhHostCountry: "BG",
        vhDriverType: "2",
        periodId: form.periodId,
        vhInsObjTypeId: form.vhInsObjTypeId,
        payType: form.payType,
        vehicle: {
          vhVin: form.vhVin,
          vhRegNo: form.vhRegNo,
          vhFirstRegDate: calcState?.vehicle.firstRegistrationDate
            ? fmt(calcState.vehicle.firstRegistrationDate)
            : fmt(form.comencingDate),
          vhManifactureYear: form.vhManifactureYear,
          vhBrandDescription: form.vhBrandDescription,
          vhModelDescription: form.vhModelDescription,
          vhSeatsCount: form.vhSeatsCount,
          vhMaxWeight: form.vhMaxWeightF1,
          vhEngineCapacity: form.vhEngineCapacity,
          vhMaxPower: form.vhMaxPower,
          vhRegistrationTypeId: form.vhRegistrationTypeId,
          vhPurposeId: form.vhPurposeId,
          vhRegCertNumber: form.vhRegCertNo,
          vhMaxWeightF1: form.vhMaxWeightF1,
        },
        holder: { ...person, custTypeId: form.custTypeId, custTztypeId: "4" },
        owner: { ...person, custTztypeId: "4" },
        // OZK requires the "usual driver" (Обичаен водач, P001-130) and vehicle-user
        // sections; the latter also needs custTypeId (S001-005). For MTPL both default
        // to the holder.
        driver: { ...person, custTypeId: form.custTypeId },
        vehicleUser: { ...person, custTypeId: form.custTypeId },
        flRenew: false,
        specialClauses: "",
        acquirer: "",
        newOwner: "",
        stikeri: {
          stiker: [
            {
              sdocTypeId: "120",
              serialNum: stripDocSeries(form.stikerSerial),
              comencingDate: fmt(form.comencingDate),
              expiringDate: fmt(
                moment(form.expiringDate, ["DD-MM-YYYY", "YYYY-MM-DD"])
                  .subtract(1, "day")
                  .format("DD-MM-YYYY")
              ),
            },
          ],
        },
        sertifikati: {
          sertifikat: [
            {
              serialNum: stripDocSeries(form.sertifikatSerial),
              comencingDate: fmtISO(form.comencingDate),
              expiringDate: fmtISO(
                moment(form.expiringDate, ["DD-MM-YYYY", "YYYY-MM-DD"])
                  .subtract(1, "day")
                  .format("DD-MM-YYYY")
              ),
              gcsTervalidityId: "5",
              sdocTypeId: "81",
            },
          ],
        },
      },
    };
  };

  // ── Issue policy ──────────────────────────────────────────────────────────

  const handleIssue = async () => {
    if (!padeji.length) return;
    setIssueLoading(true);
    try {
      const res = await OzkIntegrationService.issuePolicy(
        jwt,
        buildIssuePayload()
      );
      if (res.status?.status !== 0) {
        const msg =
          res.status?.info?.map((i) => i.description).join("; ") ??
          "Грешка при издаване";
        throw new Error(msg);
      }
      setIssuedReqId(res.reqID);
      setIssuedUpn(res.upn);
      setAlert(`Полицата е издадена успешно. УПН: ${res.upn}`);
      setAlertType(FadeAlertType.success);
    } catch (e: any) {
      setAlert(e.message ?? "Грешка при издаване на полица");
      setAlertType(FadeAlertType.error);
    } finally {
      setIssueLoading(false);
    }
  };

  // ── Print policy ──────────────────────────────────────────────────────────

  const handlePrint = async () => {
    if (!issuedReqId) return;
    setPrintLoading(true);
    try {
      const blob = await OzkIntegrationService.printPolicy(jwt, issuedReqId);
      const url = URL.createObjectURL(blob);
      window.open(url, "_blank");
      setTimeout(() => URL.revokeObjectURL(url), 10_000);
    } catch (e: any) {
      setAlert(e.message ?? "Грешка при печат");
      setAlertType(FadeAlertType.error);
    } finally {
      setPrintLoading(false);
    }
  };

  // ─────────────────────────────────────────────────────────────────────────
  // Render
  // ─────────────────────────────────────────────────────────────────────────

  return (
    <div className="mx-auto max-w-4xl space-y-4 p-4">
      <FadeAlert
        alert={alert}
        type={alertType}
        onClose={() => setAlert(null)}
      />

      {/* ── Pre-filled summary from calculator ── */}
      {calcState && (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          {/* Vehicle */}
          <fieldset className="border-stroke dark:border-strokedark shadow-default dark:bg-boxdark rounded-sm border bg-white">
            <SectionHeader title="Данни за МПС" />
            <div className="grid grid-cols-2 gap-x-4 gap-y-2 p-3 pt-2">
              <InfoRow label="Рег. номер" value={calcState.vehicle.regNo} />
              <InfoRow label="Шаси" value={calcState.vehicle.chassis} />
              <InfoRow
                label="Марка / Модел"
                value={`${calcState.vehicle.make} ${calcState.vehicle.model}`.trim()}
              />
              <InfoRow
                label="Куб. см"
                value={calcState.vehicle.displacement || "-"}
              />
              <InfoRow
                label="Мощност (к.с.)"
                value={calcState.vehicle.enginePower || "-"}
              />
              <InfoRow label="Места" value={calcState.vehicle.seats || "-"} />
              <InfoRow
                label="Год. производство"
                value={calcState.vehicle.productionYear || "-"}
              />
            </div>
          </fieldset>

          {/* Owner */}
          <fieldset className="border-stroke dark:border-strokedark shadow-default dark:bg-boxdark rounded-sm border bg-white">
            <SectionHeader title="Данни за клиент" />
            <div className="grid grid-cols-1 gap-y-2 p-3 pt-2">
              <div className="mb-1 flex items-center gap-3">
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[#243095]/10 text-sm font-bold text-[#243095]">
                  {calcState.owner.name
                    .split(" ")
                    .slice(0, 2)
                    .map((w) => w[0])
                    .join("")}
                </div>
                <div>
                  <p className="text-sm font-bold text-black dark:text-white">
                    {calcState.owner.name}
                  </p>
                  <p className="text-body dark:text-bodydark text-[11px]">
                    {calcState.owner.personType === "1"
                      ? "Физическо лице"
                      : "Юридическо лице"}
                  </p>
                </div>
              </div>
              <InfoRow
                label={calcState.owner.personType === "1" ? "ЕГН" : "ЕИК"}
                value={calcState.owner.pid}
              />
              {calcState.owner.address && (
                <InfoRow label="Адрес" value={calcState.owner.address} />
              )}
            </div>
          </fieldset>
        </div>
      )}

      {/* ── Policy parameters ── */}
      <fieldset className="border-stroke dark:border-strokedark shadow-default dark:bg-boxdark rounded-sm border bg-white">
        <SectionHeader title="Параметри на полицата" />
        <div className="space-y-4 p-4 pt-2">
          {/* Dates + period */}
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <div>
              <label className={labelClass}>Дата на договор</label>
              <DatePicker
                name="contractDate"
                value={form.contractDate}
                onChange={(e) => set("contractDate", e.target.value)}
              />
            </div>
            <div>
              <label className={labelClass}>Начало</label>
              <DatePicker
                name="comencingDate"
                value={form.comencingDate}
                onChange={(e) => set("comencingDate", e.target.value)}
              />
            </div>
            <div>
              <label className={labelClass}>Край</label>
              <DatePicker
                name="expiringDate"
                value={form.expiringDate}
                onChange={(e) => set("expiringDate", e.target.value)}
              />
            </div>
            <div>
              <label className={labelClass}>Период</label>
              <SelectGroup
                value={form.periodId}
                onChange={(v) => set("periodId", v)}
                options={{
                  d003: "3 дни",
                  d007: "7 дни",
                  d015: "15 дни",
                  d030: "30 дни",
                  d090: "90 дни",
                  d180: "180 дни",
                  m001: "1 месец",
                  m003: "3 месеца",
                  m006: "6 месеца",
                  y001: "1 година",
                  y002: "2 години",
                }}
              />
            </div>
          </div>

          {/* Vehicle identifiers (editable) */}
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <div>
              <label className={labelClass}>Рег. номер</label>
              <input
                name="vhRegNo"
                value={form.vhRegNo}
                onChange={handleChange}
                className={`${inputClass} uppercase tracking-widest`}
              />
            </div>
            <div>
              <label className={labelClass}>№ на свидетелство</label>
              <input
                name="vhRegCertNo"
                value={form.vhRegCertNo}
                onChange={handleChange}
                className={inputClass}
              />
            </div>
            <div>
              <label className={labelClass}>VIN / Шаси</label>
              <input
                name="vhVin"
                value={form.vhVin}
                onChange={handleChange}
                className={inputClass}
              />
            </div>
            <div>
              <label className={labelClass}>Предназначение</label>
              <SelectGroup
                value={form.vhPurposeId}
                onChange={(v) => set("vhPurposeId", v)}
                options={
                  Object.keys(vhPurposes).length
                    ? vhPurposes
                    : { "1": "Лично ползване" }
                }
              />
            </div>
          </div>

          {/* Vehicle specs */}
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
            <div>
              <label className={labelClass}>Год. производство</label>
              <input
                type="number"
                name="vhManifactureYear"
                value={form.vhManifactureYear}
                onChange={handleChange}
                className={inputClass}
              />
            </div>
            <div>
              <label className={labelClass}>Места</label>
              <input
                type="number"
                name="vhSeatsCount"
                value={form.vhSeatsCount}
                onChange={handleChange}
                className={inputClass}
              />
            </div>
            <div>
              <label className={labelClass}>Куб. см</label>
              <input
                type="number"
                name="vhEngineCapacity"
                value={form.vhEngineCapacity}
                onChange={handleChange}
                className={inputClass}
              />
            </div>
            <div>
              <label className={labelClass}>Мощност (к.с.)</label>
              <input
                type="number"
                name="vhMaxPower"
                value={form.vhMaxPower}
                onChange={handleChange}
                className={inputClass}
              />
            </div>
            <div>
              <label className={labelClass}>Марка</label>
              <input
                name="vhBrandDescription"
                value={form.vhBrandDescription}
                onChange={handleChange}
                className={inputClass}
              />
            </div>
            <div>
              <label className={labelClass}>Модел</label>
              <input
                name="vhModelDescription"
                value={form.vhModelDescription}
                onChange={handleChange}
                className={inputClass}
              />
            </div>
          </div>

          {/* Client */}
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
            <div>
              <label className={labelClass}>ЕГН / ЕИК</label>
              <input
                name="custPin"
                value={form.custPin}
                onChange={handleChange}
                className={inputClass}
              />
            </div>
            <div>
              <label className={labelClass}>Тип лице</label>
              <SelectGroup
                value={form.custTypeId}
                onChange={(v) => set("custTypeId", v)}
                options={
                  Object.keys(custTypes).length
                    ? custTypes
                    : { C: "Физическо", F: "Юридическо" }
                }
              />
            </div>
            <div>
              <label className={labelClass}>Тип лице ОЗК</label>
              <SelectGroup
                value={form.custFtypeId}
                onChange={(v) => set("custFtypeId", v)}
                options={
                  Object.keys(ftypes).length ? ftypes : { "1010": "1010" }
                }
              />
            </div>
            <div>
              <label className={labelClass}>Собствено</label>
              <input
                name="custName"
                value={form.custName}
                onChange={handleChange}
                className={inputClass}
              />
            </div>
            <div>
              <label className={labelClass}>Презиме</label>
              <input
                name="custSurname"
                value={form.custSurname}
                onChange={handleChange}
                className={inputClass}
              />
            </div>
            <div>
              <label className={labelClass}>Фамилия</label>
              <input
                name="custFamily"
                value={form.custFamily}
                onChange={handleChange}
                className={inputClass}
              />
            </div>
            <div>
              <label className={labelClass}>Улица</label>
              <input
                name="custStreet"
                value={form.custStreet}
                onChange={handleChange}
                className={inputClass}
              />
            </div>
            <div>
              <label className={labelClass}>Пощенски код</label>
              <input
                name="custPostcode"
                value={form.custPostcode}
                onChange={handleChange}
                className={inputClass}
              />
            </div>
            <div>
              <label className={labelClass}>Нас. място (ЕКАТТЕ)</label>
              <SelectGroup
                value={form.custLocationId}
                onChange={(v) => set("custLocationId", v)}
                options={Object.keys(cities).length ? cities : {}}
                placeholder="Изберете град"
              />
            </div>
          </div>

          {/* Get tariff button */}
          <div className="flex justify-end">
            <button
              type="button"
              onClick={fetchTariff}
              disabled={tariffLoading}
              className="flex items-center gap-2 rounded bg-[#3C50E0] px-5 py-2 text-sm font-semibold text-white hover:bg-opacity-90 disabled:opacity-50"
            >
              {tariffLoading ? (
                <Spinner />
              ) : (
                <svg
                  className="h-4 w-4"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={2}
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 11h.01M12 11h.01M15 11h.01M4 19h16a2 2 0 002-2V7a2 2 0 00-2-2H4a2 2 0 00-2 2v10a2 2 0 002 2z"
                  />
                </svg>
              )}
              {tariffLoading ? "Тарифиране..." : "Поискай оферта"}
            </button>
          </div>
        </div>
      </fieldset>

      {/* ── Tariff error ── */}
      {tariffError && (
        <div className="flex items-start gap-2 rounded-sm border border-[#AA2808]/30 bg-red-50 p-3 dark:bg-red-900/10">
          <svg
            className="mt-0.5 h-4 w-4 shrink-0 text-[#AA2808]"
            fill="currentColor"
            viewBox="0 0 20 20"
          >
            <path
              fillRule="evenodd"
              d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
              clipRule="evenodd"
            />
          </svg>
          <p className="text-sm text-[#AA2808]">{tariffError}</p>
        </div>
      )}

      {/* ── Installment picker ── */}
      {padeji.length > 0 && (
        <fieldset className="border-stroke dark:border-strokedark shadow-default dark:bg-boxdark rounded-sm border bg-white">
          <SectionHeader title="Начин на плащане" />
          <div className="p-4 pt-2">
            <InstallmentPicker
              padeji={padeji}
              total={calculatedPremium}
              selected={form.numberOfInstallments}
              onSelect={(n) => set("numberOfInstallments", n)}
            />
          </div>
        </fieldset>
      )}

      {/* ── Sticker / certificate serial numbers ── */}
      {padeji.length > 0 && (
        <fieldset className="border-stroke dark:border-strokedark shadow-default dark:bg-boxdark rounded-sm border bg-white">
          <SectionHeader title="Стикер и сертификат" />
          <div className="grid grid-cols-1 gap-3 p-4 pt-2 sm:grid-cols-2">
            <div>
              <label className={labelClass}>Стикер (сер. номер)</label>
              <input
                type="text"
                name="stikerSerial"
                value={form.stikerSerial}
                onChange={handleChange}
                className={inputClass}
                placeholder="напр. G1960603562 (серия G1, 9 цифри)"
              />
            </div>
            <div>
              <label className={labelClass}>
                Сертификат за зелена карта (сер. номер)
              </label>
              <input
                type="text"
                name="sertifikatSerial"
                value={form.sertifikatSerial}
                onChange={handleChange}
                className={inputClass}
                placeholder="напр. AA023394 (серия AA, 6 цифри)"
              />
            </div>
          </div>
        </fieldset>
      )}

      {/* ── Issue + Print ── */}
      {padeji.length > 0 && (
        <div className="flex items-center justify-end gap-3">
          {issuedReqId && (
            <button
              type="button"
              onClick={handlePrint}
              disabled={printLoading}
              className="flex items-center gap-2 rounded border border-[#243095] px-5 py-2 text-sm font-semibold text-[#243095] hover:bg-[#243095]/5 disabled:opacity-50 dark:border-white dark:text-white"
            >
              {printLoading ? (
                <Spinner />
              ) : (
                <svg
                  className="h-4 w-4"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={2}
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z"
                  />
                </svg>
              )}
              {printLoading ? "Печат..." : `Принт (УПН: ${issuedUpn})`}
            </button>
          )}

          <button
            type="button"
            onClick={handleIssue}
            disabled={issueLoading || !!issuedReqId}
            className="flex items-center gap-2 rounded bg-[#243095] px-6 py-2 text-sm font-semibold text-white hover:bg-opacity-90 disabled:opacity-50"
          >
            {issueLoading ? (
              <Spinner />
            ) : (
              <svg
                className="h-4 w-4"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
            )}
            {issueLoading
              ? "Издаване..."
              : issuedReqId
              ? "Издадена ✓"
              : "Издай полица"}
          </button>
        </div>
      )}
    </div>
  );
};

export default Ozk;
